import {useContext} from "react";
import {observer} from "mobx-react";
import {Field} from "@headlessui/react";
import {type SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useQueryClient} from "@tanstack/react-query";
import {Input, Button, Label, FieldError, PasswordInput} from "@chayka/ui";
import {SignUpStateContext, SignUpFirstSchema, type SignUpFirstData} from "./state";
import {trpc} from "@/app/trpc";
import {showErrorToast} from "@/utils/toast";
import {Already} from "./Already";

interface SignUpFirstStepProps {
  onNext: () => void;
}

export const SignUpFirstStep = observer(({onNext}: SignUpFirstStepProps) => {
  const queryClient = useQueryClient();

  const {firstStepData, setFirstStepData} = useContext(SignUpStateContext);

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: {errors, isSubmitting},
  } = useForm<SignUpFirstData>({
    resolver: zodResolver(SignUpFirstSchema),
    defaultValues: {
      name: firstStepData.name,
      login: firstStepData.login,
      email: firstStepData.email,
      password: firstStepData.password,
      passwordRepeat: firstStepData.passwordRepeat,
    },
  });

  const onSubmit: SubmitHandler<SignUpFirstData> = async (data) => {
    clearErrors(["email", "login"]);

    try {
      const [isEmailAvailable, isLoginAvailable] = await Promise.all([
        queryClient.fetchQuery({
          ...trpc.auth.checkEmail.queryOptions({email: data.email}),
          staleTime: 0,
        }),
        queryClient.fetchQuery({
          ...trpc.auth.checkLogin.queryOptions({login: data.login}),
          staleTime: 0,
        }),
      ]);

      let hasAvailabilityError = false;

      if (!isEmailAvailable) {
        setError("email", {
          type: "manual",
          message: "Этот email уже используется",
        });
        hasAvailabilityError = true;
      }

      if (!isLoginAvailable) {
        setError("login", {
          type: "manual",
          message: "Этот логин уже занят",
        });
        hasAvailabilityError = true;
      }

      if (hasAvailabilityError) {
        return;
      }
    } catch {
      showErrorToast("Не удалось проверить email и логин. Попробуйте снова.");
      return;
    }

    setFirstStepData(data);
    onNext();
  };

  return (
    <>
      <Field>
        <Label required>Имя</Label>
        <Input placeholder="Введите имя" {...register("name")} />
        <FieldError error={errors.name?.message} />
      </Field>
      <Field>
        <Label required>Логин</Label>
        <Input placeholder="Введите логин" {...register("login")} />
        <FieldError error={errors.login?.message} />
      </Field>
      <Field>
        <Label required>Email</Label>
        <Input placeholder="Введите email" type="email" {...register("email")} />
        <FieldError error={errors.email?.message} />
      </Field>
      <Field>
        <Label required>Пароль</Label>
        <PasswordInput placeholder="Введите пароль" {...register("password")} />
        <FieldError error={errors.password?.message} />
      </Field>
      <Field>
        <Label required>Подтверждение пароля</Label>
        <PasswordInput placeholder="Повторите пароль" {...register("passwordRepeat")} />
        <FieldError error={errors.passwordRepeat?.message} />
      </Field>
      <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting}>
        Далее
      </Button>
      <Already />
    </>
  );
});
